<script setup lang="ts">
import { computed, ref, watchEffect } from "vue";

import { isset } from "@/helpers/object";

import ButtonAlt from "@/dsComponents/buttons/ButtonAlt.vue";
import Tag from "@/dsComponents/Tag";

import { validate } from "./helpers";
import TextInputAlt from "./TextInputAlt.vue";

export interface TagInputProps {
    label?: string;
    modelValue: string[];
    type?: "number" | "text" | "email" | "tel" | "nir" | "evoliaId" | "date";
    description?: string;
    placeholder?: string;
    error?: string;
    size?: "sm" | "md" | "lg";
    validate?: boolean;
    disabled?: boolean;
    required?: boolean;
    enableLabel?: boolean;
    onAdd?: (item: string) => void;
    onRemove?: (item: string) => void;
    buttonLabel?: string;
    name?: string;
    addButtonType?: "neutral" | "primary";
}

const props = withDefaults(defineProps<TagInputProps>(), {
    size: "md",
    buttonLabel: "Ajouter",
    type: "text",
    validate: true,
    addButtonType: "neutral",
});

const inputValue = ref("");
const isButtonActive = computed(() => inputValue.value.trim() !== "");

// Handle parent v-model value update
const emit = defineEmits(["update:modelValue"]);

const localError = ref<string | undefined>("");
watchEffect(() => {
    localError.value = props.error;
});

const handleInputChange = (value: string) => {
    inputValue.value = value;
    if (localError.value && inputValue && props.validate) {
        const { error } = validate(value, props.type, props.required);
        localError.value = error;
    }
};

const handleAdd = () => {
    if (!isset(inputValue.value)) return;
    // Use onAdd function if provided
    if (props.onAdd) return props.onAdd(inputValue.value.trim());
    const { error } = validate(inputValue.value, props.type);
    localError.value = error;
    if (!error) {
        // Else emit input to use v-model
        emit("update:modelValue", [...props.modelValue, inputValue.value.trim()]);
        inputValue.value = "";
    }
};

const handleEnterAdd = () => {
    if (props.disabled) {
        return;
    }
    if (!isButtonActive.value) {
        return;
    }
    handleAdd();
};

const handleRemove = (index: number) => {
    if (props.disabled) return;
    // Use onRemove function if provided
    if (props.onRemove) return props.onRemove(inputValue.value);
    // Else emit input to use v-model
    emit(
        "update:modelValue",
        props.modelValue.filter((_, i) => index !== i)
    );
};
</script>
<template>
    <div class="ds-tag-input">
        <div class="input-button-container">
            <div class="input-button">
                <div class="ds-tag-input__field" @keydown.enter.prevent="handleEnterAdd">
                    <TextInputAlt
                        v-bind="props"
                        size="xs"
                        variant="stroke"
                        :state="inputValue.trim() ? 'filled' : 'empty'"
                        type="text"
                        :modelValue="inputValue"
                        :name="name ?? 'tag-input'"
                        :validate="false"
                        :error="undefined"
                        @update:model-value="handleInputChange"
                    />
                </div>
                <ButtonAlt
                    size="sm"
                    variant="filled"
                    :type="addButtonType"
                    left-icon="plus"
                    :disabled="!isButtonActive"
                    @click="handleAdd"
                >
                    {{ buttonLabel }}
                </ButtonAlt>
            </div>
            <div v-if="localError" class="input-error">{{ localError }}</div>
        </div>
        <div v-if="!localError && modelValue.length > 0" class="tag-container">
            <div v-for="(item, index) of modelValue" :key="index">
                <Tag @click="() => handleRemove(index)" :label="item" :closable="!disabled" />
            </div>
        </div>
    </div>
</template>
<style scoped lang="scss">
.ds-tag-input {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 8px;
    .input-button-container {
        position: relative;
        .input-button {
            display: flex;
            padding: var(--spacing-xs, 4px);
            align-items: center;
            gap: 10px;
        }

        .ds-tag-input__field {
            flex: 1 1 0;
            min-width: 0;
        }
    }
    .tag-container {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
    }

    div.input-error {
        @include font-size-12;
        position: absolute;
        color: $red-base;
        bottom: -20px;
        left: 0;
        padding: 0;
    }
}
</style>
